import { useState, useCallback, useMemo } from "react";
import { View, Text, Pressable, ScrollView, Modal } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { X, SlidersHorizontal, Globe } from "lucide-react-native";
import type {
  ProviderFilters,
  ConsultationType,
  OrganizationType,
  ClinicSetting,
} from "@louise/providers/providers.types";
import { CLINIC_SETTING_OPTIONS } from "@louise/providers/providers.types";
import {
  useOrganizations,
  getOrganizationCountryLabel,
} from "@louise/providers/hooks/useOrganizations";
import { EMBER } from "../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  BrownButton,
  IconButton,
  MoonFilterChip,
  TextActionButton,
} from "@louise/moon-ui";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
/* ─── Filter options ──────────────────────────────────────────────── */

// `value` must match the treatment names in the provider data exactly,
// otherwise the chip filters to zero clinics. Every option below maps to a
// treatment that real clinics in the dataset carry.
function getTreatmentOptions(t: TranslationFunction) {
  return [
    {
      label: t("access.find_care.filters.treatment.ivf_icsi"),
      value: "IVF & ICSI",
    },
    { label: t("access.find_care.filters.treatment.iui"), value: "IUI" },
    {
      label: t("access.find_care.filters.treatment.egg_freezing"),
      value: "Egg Freezing",
    },
    {
      label: t("access.find_care.filters.treatment.embryo_freezing"),
      value: "Embryo Freezing",
    },
    {
      label: t("access.find_care.filters.treatment.sperm_freezing"),
      value: "Sperm Freezing",
    },
    {
      label: t("access.find_care.filters.treatment.fertility_preservation"),
      value: "Fertility preservation",
    },
    { label: t("access.find_care.filters.treatment.pgt"), value: "PGT" },
    {
      label: t("access.find_care.filters.treatment.donor_programs"),
      value: "Donor programs",
    },
    {
      label: t("access.find_care.filters.treatment.surrogacy"),
      value: "Surrogacy",
    },
    {
      label: t("access.find_care.filters.treatment.ovulation_induction"),
      value: "Ovulation Induction",
    },
  ];
}

function getConsultationOptions(t: TranslationFunction) {
  return [
    {
      label: t("access.find_care.filters.consultation.in_person"),
      value: "in-person" as ConsultationType,
    },
    {
      label: t("access.find_care.filters.consultation.telehealth"),
      value: "telehealth" as ConsultationType,
    },
  ];
}

function getClinicTypeOptions(t: TranslationFunction) {
  return [
    {
      label: t("access.find_care.filters.clinic_type.fertility_clinic"),
      value: "fertility-clinic" as OrganizationType,
    },
    {
      label: t("access.find_care.filters.clinic_type.medical_practice"),
      value: "medical-practice" as OrganizationType,
    },
    {
      label: t("access.find_care.filters.clinic_type.solo_practitioner"),
      value: "solo-practitioner" as OrganizationType,
    },
    {
      label: t("access.find_care.filters.clinic_type.wellness_provider"),
      value: "wellness-provider" as OrganizationType,
    },
    {
      label: t("access.find_care.filters.clinic_type.lab"),
      value: "lab" as OrganizationType,
    },
    {
      label: t("access.find_care.filters.clinic_type.pharmacy"),
      value: "pharmacy" as OrganizationType,
    },
  ];
}

function getSpecialtyOptions(t: TranslationFunction) {
  return [
    {
      label: t("access.find_care.filters.specialty.reproductive_endocrinology"),
      value: "reproductive-endocrinology",
    },
    {
      label: t("access.find_care.filters.specialty.pmos"),
      value: "PMOS (ex PCOS)",
    },
    {
      label: t("access.find_care.filters.specialty.male_fertility"),
      value: "male-fertility",
    },
    {
      label: t("access.find_care.filters.specialty.egg_freezing"),
      value: "egg-freezing",
    },
    {
      label: t("access.find_care.filters.specialty.genetics"),
      value: "genetics",
    },
    {
      label: t("access.find_care.filters.specialty.fertility_acupuncture"),
      value: "fertility-acupuncture",
    },
    {
      label: t("access.find_care.filters.specialty.fertility_nutrition"),
      value: "fertility-nutrition",
    },
    {
      label: t("access.find_care.filters.specialty.urology"),
      value: "urology",
    },
    { label: t("access.find_care.filters.specialty.ob_gyn"), value: "ob-gyn" },
    {
      label: t("access.find_care.filters.specialty.pelvic_medicine"),
      value: "pelvic-medicine",
    },
    {
      label: t("access.find_care.filters.specialty.microsurgery"),
      value: "microsurgery",
    },
    {
      label: t("access.find_care.filters.specialty.hormone_management"),
      value: "hormone-management",
    },
  ];
}

// `value` must match the language names in the provider data; `flag` is
// display-only and prepended to the chip label.
function getLanguageOptions(t: TranslationFunction) {
  return [
    {
      value: "English",
      label: t("access.find_care.filters.language.english"),
      flag: "🇬🇧",
    },
    {
      value: "Spanish",
      label: t("access.find_care.filters.language.spanish"),
      flag: "🇪🇸",
    },
    {
      value: "Mandarin",
      label: t("access.find_care.filters.language.mandarin"),
      flag: "🇨🇳",
    },
    {
      value: "Korean",
      label: t("access.find_care.filters.language.korean"),
      flag: "🇰🇷",
    },
    {
      value: "Russian",
      label: t("access.find_care.filters.language.russian"),
      flag: "🇷🇺",
    },
    {
      value: "French",
      label: t("access.find_care.filters.language.french"),
      flag: "🇫🇷",
    },
    {
      value: "Arabic",
      label: t("access.find_care.filters.language.arabic"),
      flag: "🇸🇦",
    },
    {
      value: "Vietnamese",
      label: t("access.find_care.filters.language.vietnamese"),
      flag: "🇻🇳",
    },
    {
      value: "Japanese",
      label: t("access.find_care.filters.language.japanese"),
      flag: "🇯🇵",
    },
    {
      value: "Portuguese",
      label: t("access.find_care.filters.language.portuguese"),
      flag: "🇵🇹",
    },
    {
      value: "German",
      label: t("access.find_care.filters.language.german"),
      flag: "🇩🇪",
    },
    {
      value: "Italian",
      label: t("access.find_care.filters.language.italian"),
      flag: "🇮🇹",
    },
  ];
}

function getPatientGroupOptions(t: TranslationFunction) {
  return [
    {
      label: t("access.find_care.filters.patient_group.lgbtq_friendly"),
      value: "LGBTQ+ friendly",
    },
    {
      label: t("access.find_care.filters.patient_group.lgbtqia"),
      value: "LGBTQIA+",
    },
    {
      label: t("access.find_care.filters.patient_group.single_parents"),
      value: "Single Parents",
    },
    {
      label: t("access.find_care.filters.patient_group.same_sex_couples"),
      value: "Same sex couples",
    },
  ];
}

// Curated top insurance carriers. Matching is space/case-insensitive and by
// substring (see useOrganizations), so "Kaiser" also catches "Kaiser
// Permanente" and "Blue Cross Blue Shield" catches its regional variants.
const CARRIER_OPTIONS: string[] = [
  "Aetna",
  "United Healthcare",
  "Cigna",
  "Blue Cross Blue Shield",
  "Anthem",
  "Humana",
  "Kaiser",
  "Tricare",
  "Oxford",
  "Health Net",
];

// Fertility benefit programs (employer-sponsored coverage).
const BENEFIT_OPTIONS: string[] = [
  "Progyny",
  "Carrot",
  "Maven",
  "WINFertility",
  "Kindbody",
  "Stork Club",
];

/* ─── Types ───────────────────────────────────────────────────────── */

type Props = {
  filters: ProviderFilters;
  onFiltersChange: (filters: ProviderFilters) => void;
  homeCountry: string;
  includeHomeCountry: boolean;
  includeInternational: boolean;
  internationalCountries: string[];
  internationalOptions: { value: string; label: string }[];
  onLocationScopeChange: (next: {
    includeHomeCountry: boolean;
    includeInternational: boolean;
    internationalCountries: string[];
  }) => void;
};

function countryLabel(country: string) {
  return getOrganizationCountryLabel(country);
}

/* ─── Main component ──────────────────────────────────────────────── */

export default function MoonClinicsFilters({
  filters,
  onFiltersChange,
  homeCountry,
  includeHomeCountry,
  includeInternational,
  internationalCountries,
  internationalOptions,
  onLocationScopeChange,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MoonClinicsFilters");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ProviderFilters>(filters);
  const [draftIncludeHomeCountry, setDraftIncludeHomeCountry] =
    useState(includeHomeCountry);
  const [draftIncludeInternational, setDraftIncludeInternational] =
    useState(includeInternational);
  const [draftInternationalCountries, setDraftInternationalCountries] =
    useState<string[]>(internationalCountries);
  const treatmentOptions = useMemo(() => getTreatmentOptions(t), [t]);
  const consultationOptions = useMemo(() => getConsultationOptions(t), [t]);
  const clinicTypeOptions = useMemo(() => getClinicTypeOptions(t), [t]);
  const specialtyOptions = useMemo(() => getSpecialtyOptions(t), [t]);
  const languageOptions = useMemo(() => getLanguageOptions(t), [t]);
  const patientGroupOptions = useMemo(() => getPatientGroupOptions(t), [t]);

  // Live preview count
  const previewFilters = useMemo(() => {
    const countries = [
      ...(draftIncludeHomeCountry ? [homeCountry] : []),
      ...(draftIncludeInternational ? draftInternationalCountries : []),
    ];
    return {
      ...draft,
      country: countries.length > 0 ? countries : ["__none__"],
    };
  }, [
    draft,
    homeCountry,
    draftIncludeHomeCountry,
    draftIncludeInternational,
    draftInternationalCountries,
  ]);
  const { totalFiltered, isLoading: isLoadingPreview } =
    useOrganizations(previewFilters);

  const handleOpen = useCallback(() => {
    setDraft({ ...filters });
    setDraftIncludeHomeCountry(includeHomeCountry);
    setDraftIncludeInternational(includeInternational);
    setDraftInternationalCountries(internationalCountries);
    setOpen(true);
  }, [
    filters,
    includeHomeCountry,
    includeInternational,
    internationalCountries,
  ]);

  const handleApply = useCallback(() => {
    // Preserve search from current filters
    const nextFilters: ProviderFilters = { ...draft, search: filters.search };
    delete nextFilters.country;
    onFiltersChange(nextFilters);
    onLocationScopeChange({
      includeHomeCountry: draftIncludeHomeCountry,
      includeInternational: draftIncludeInternational,
      internationalCountries: draftInternationalCountries,
    });
    setOpen(false);
  }, [
    draft,
    filters.search,
    onFiltersChange,
    onLocationScopeChange,
    draftIncludeHomeCountry,
    draftIncludeInternational,
    draftInternationalCountries,
  ]);

  const handleClear = useCallback(() => {
    setDraft({ search: filters.search });
    setDraftIncludeHomeCountry(true);
    setDraftIncludeInternational(false);
    setDraftInternationalCountries(
      internationalOptions.map((option) => option.value),
    );
  }, [filters.search, internationalOptions]);

  const toggleTreatment = useCallback((treatment: string) => {
    setDraft((prev) => {
      const current = prev.treatments ?? [];
      const has = current.includes(treatment);
      return {
        ...prev,
        treatments: has
          ? current.filter((t) => t !== treatment)
          : [...current, treatment],
      };
    });
  }, []);

  const toggleConsultation = useCallback((type: ConsultationType) => {
    setDraft((prev) => {
      const current = prev.consultationTypes ?? [];
      const has = current.includes(type);
      return {
        ...prev,
        consultationTypes: has
          ? current.filter((t) => t !== type)
          : [...current, type],
      };
    });
  }, []);

  const toggleClinicType = useCallback((type: OrganizationType) => {
    setDraft((prev) => {
      const current = prev.type ?? [];
      const has = current.includes(type);
      return {
        ...prev,
        type: has ? current.filter((t) => t !== type) : [...current, type],
      };
    });
  }, []);

  const toggleSetting = useCallback((setting: ClinicSetting) => {
    setDraft((prev) => {
      const current = prev.settings ?? [];
      const has = current.includes(setting);
      return {
        ...prev,
        settings: has
          ? current.filter((s) => s !== setting)
          : [...current, setting],
      };
    });
  }, []);

  const toggleArrayFilter = useCallback(
    (
      field:
        | "specialties"
        | "languages"
        | "patientGroups"
        | "carriers"
        | "employeeBenefits",
      value: string,
    ) => {
      setDraft((prev) => {
        const current = (prev[field] as string[] | undefined) ?? [];
        const has = current.includes(value);
        return {
          ...prev,
          [field]: has
            ? current.filter((v) => v !== value)
            : [...current, value],
        };
      });
    },
    [],
  );

  const toggleInternationalCountry = useCallback((country: string) => {
    setDraftInternationalCountries((prev) =>
      prev.includes(country)
        ? prev.filter((item) => item !== country)
        : [...prev, country],
    );
  }, []);

  const homeCountryOption = useMemo(
    () => ({ value: homeCountry, label: countryLabel(homeCountry) }),
    [homeCountry],
  );

  // Count active filters on main button
  const mainActiveCount = useMemo(() => {
    let count = 0;
    if (filters.treatments?.length) count += filters.treatments.length;
    if (filters.consultationTypes?.length) {
      count += filters.consultationTypes.length;
    }
    if (filters.type?.length) count += filters.type.length;
    if (filters.settings?.length) count += filters.settings.length;
    if (filters.specialties?.length) count += filters.specialties.length;
    if (filters.languages?.length) count += filters.languages.length;
    if (filters.patientGroups?.length) count += filters.patientGroups.length;
    if (filters.carriers?.length) count += filters.carriers.length;
    if (filters.employeeBenefits?.length) {
      count += filters.employeeBenefits.length;
    }
    if (filters.insuranceAccepted) count++;
    return count;
  }, [filters]);

  return (
    <>
      {/* Filter button */}
      <Pressable
        onPress={() => {
          // Icon-only filter button with no visible copy to name it.
          capturePress("open_clinic_filters");
          handleOpen();
        }}
        accessibilityRole="button"
        testID="find-care-filters-button"
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          ...(GLASS_CARD_STYLE as object),
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SlidersHorizontal
          size={18}
          color={EMBER.colors.textLight}
          strokeWidth={1.5}
        />
        {mainActiveCount > 0 && (
          <View
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: EMBER.colors.warm,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 10, fontWeight: "700", color: "#fff" }}
            >
              {mainActiveCount}
            </Text>
          </View>
        )}
      </Pressable>

      {/* Filter Modal */}
      <Modal
        visible={open}
        transparent
        animationType="slide"
        onRequestClose={() => setOpen(false)}
      >
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)" }}>
          <View style={{ flex: 0.08 }}>
            <Pressable style={{ flex: 1 }} onPress={() => setOpen(false)} />
          </View>
          <View
            style={{
              flex: 1,
              backgroundColor: EMBER.colors.surface,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              overflow: "hidden",
            }}
          >
            <SafeAreaView style={{ flex: 1 }} edges={["bottom"]}>
              {/* Header */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingHorizontal: 20,
                  paddingTop: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: "rgba(0,0,0,0.06)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 22,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  {t("access.find_care.filters.title")}
                </Text>
                <IconButton
                  onPress={() => setOpen(false)}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0,0,0,0.05)"
                  pressedBackgroundColor="rgba(0,0,0,0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Content */}
              <ScrollView
                contentContainerStyle={{
                  paddingHorizontal: 20,
                  paddingBottom: 20,
                }}
                showsVerticalScrollIndicator={false}
              >
                {/* Location Scope */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 20,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.location_scope")}
                </Text>
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t("access.find_care.filters.include_international"),
                      ),
                      "access.find_care.filters.include_international",
                    );
                    setDraftIncludeInternational((current) => !current);
                  }}
                  accessibilityRole="switch"
                  accessibilityState={{ checked: draftIncludeInternational }}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: 16,
                    backgroundColor: draftIncludeInternational
                      ? `${EMBER.colors.cryo}10`
                      : "rgba(0,0,0,0.035)",
                    borderWidth: 1,
                    borderColor: draftIncludeInternational
                      ? `${EMBER.colors.cryo}28`
                      : "rgba(0,0,0,0.06)",
                  }}
                >
                  <Globe
                    size={18}
                    color={
                      draftIncludeInternational
                        ? EMBER.colors.cryo
                        : EMBER.colors.textMid
                    }
                    strokeWidth={1.8}
                  />
                  <View style={{ flex: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "700",
                        color: EMBER.colors.text,
                      }}
                    >
                      {t("access.find_care.filters.include_international")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textLight,
                        marginTop: 2,
                      }}
                    >
                      {draftIncludeInternational
                        ? draftIncludeHomeCountry
                          ? t(
                              "access.find_care.filters.scope_home_plus_selected",
                              {
                                params: { country: countryLabel(homeCountry) },
                              },
                            )
                          : t("access.find_care.filters.scope_selected_only")
                        : t("access.find_care.filters.scope_home_only", {
                            params: { country: countryLabel(homeCountry) },
                          })}
                    </Text>
                  </View>
                  <View
                    style={{
                      width: 42,
                      height: 24,
                      borderRadius: 12,
                      padding: 2,
                      backgroundColor: draftIncludeInternational
                        ? EMBER.colors.cryo
                        : "rgba(59,26,8,0.14)",
                      alignItems: draftIncludeInternational
                        ? "flex-end"
                        : "flex-start",
                      justifyContent: "center",
                    }}
                  >
                    <View
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 10,
                        backgroundColor: "#FFFFFF",
                      }}
                    />
                  </View>
                </Pressable>
                {draftIncludeInternational &&
                  internationalOptions.length > 0 && (
                    <View
                      style={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      <MoonFilterChip
                        key={homeCountryOption.value}
                        label={homeCountryOption.label}
                        selected={draftIncludeHomeCountry}
                        onPress={() =>
                          setDraftIncludeHomeCountry((current) => !current)
                        }
                      />
                      {internationalOptions.map((option) => (
                        <MoonFilterChip
                          key={option.value}
                          label={option.label}
                          selected={draftInternationalCountries.includes(
                            option.value,
                          )}
                          onPress={() =>
                            toggleInternationalCountry(option.value)
                          }
                        />
                      ))}
                    </View>
                  )}

                {/* Treatments */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.treatments")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {treatmentOptions.map((opt) => (
                    <MoonFilterChip
                      key={opt.value}
                      label={opt.label}
                      selected={draft.treatments?.includes(opt.value) ?? false}
                      onPress={() => toggleTreatment(opt.value)}
                    />
                  ))}
                </View>

                {/* Consultation Type */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.consultation_type")}
                </Text>
                <View style={{ flexDirection: "row", gap: 8 }}>
                  {consultationOptions.map((opt) => (
                    <MoonFilterChip
                      key={opt.value}
                      label={opt.label}
                      selected={
                        draft.consultationTypes?.includes(opt.value) ?? false
                      }
                      onPress={() => toggleConsultation(opt.value)}
                    />
                  ))}
                </View>

                {/* Clinic Type */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.clinic_type")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {clinicTypeOptions.map((opt) => (
                    <MoonFilterChip
                      key={opt.value}
                      label={opt.label}
                      selected={draft.type?.includes(opt.value) ?? false}
                      onPress={() => toggleClinicType(opt.value)}
                    />
                  ))}
                </View>

                {/* Practice Setting */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.practice_setting")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {CLINIC_SETTING_OPTIONS.map((setting) => (
                    <MoonFilterChip
                      key={setting}
                      label={t(`access.find_care.filters.setting.${setting}`)}
                      selected={draft.settings?.includes(setting) ?? false}
                      onPress={() => toggleSetting(setting)}
                    />
                  ))}
                </View>

                {/* Specialties */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.specialties")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {specialtyOptions.map((s) => (
                    <MoonFilterChip
                      key={s.value}
                      label={s.label}
                      selected={draft.specialties?.includes(s.value) ?? false}
                      onPress={() => toggleArrayFilter("specialties", s.value)}
                    />
                  ))}
                </View>

                {/* Languages */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.languages")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {languageOptions.map((lang) => (
                    <MoonFilterChip
                      key={lang.value}
                      label={`${lang.flag}  ${lang.label}`}
                      selected={draft.languages?.includes(lang.value) ?? false}
                      onPress={() => toggleArrayFilter("languages", lang.value)}
                    />
                  ))}
                </View>

                {/* Inclusive Care */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.inclusive_care")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {patientGroupOptions.map((group) => (
                    <MoonFilterChip
                      key={group.value}
                      label={group.label}
                      selected={
                        draft.patientGroups?.includes(group.value) ?? false
                      }
                      onPress={() =>
                        toggleArrayFilter("patientGroups", group.value)
                      }
                    />
                  ))}
                </View>

                {/* Insurance Carrier */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.insurance_carrier")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {CARRIER_OPTIONS.map((carrier) => (
                    <MoonFilterChip
                      key={carrier}
                      label={carrier}
                      selected={draft.carriers?.includes(carrier) ?? false}
                      onPress={() => toggleArrayFilter("carriers", carrier)}
                    />
                  ))}
                </View>

                {/* Fertility Benefits */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.fertility_benefits")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {BENEFIT_OPTIONS.map((benefit) => (
                    <MoonFilterChip
                      key={benefit}
                      label={benefit}
                      selected={
                        draft.employeeBenefits?.includes(benefit) ?? false
                      }
                      onPress={() =>
                        toggleArrayFilter("employeeBenefits", benefit)
                      }
                    />
                  ))}
                </View>

                {/* Other */}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.textMid,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    marginTop: 28,
                    marginBottom: 12,
                  }}
                >
                  {t("access.find_care.filters.section.other")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  <MoonFilterChip
                    label={t("access.find_care.filters.accepts_insurance")}
                    selected={draft.insuranceAccepted ?? false}
                    onPress={() =>
                      setDraft((prev) => ({
                        ...prev,
                        insuranceAccepted: !prev.insuranceAccepted,
                      }))
                    }
                  />
                </View>
              </ScrollView>

              {/* Footer */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  paddingHorizontal: 20,
                  paddingVertical: 16,
                  borderTopWidth: 1,
                  borderTopColor: "rgba(0,0,0,0.06)",
                }}
              >
                <TextActionButton
                  label={t("access.find_care.filters.clear_all")}
                  onPress={handleClear}
                  color={EMBER.colors.textMid}
                />
                <BrownButton
                  label={
                    isLoadingPreview
                      ? t("access.find_care.filters.loading_providers")
                      : t(
                          totalFiltered === 1
                            ? "access.find_care.filters.show_provider_one"
                            : "access.find_care.filters.show_provider_other",
                          { params: { count: String(totalFiltered) } },
                        )
                  }
                  onPress={handleApply}
                  shape="pill"
                  fullWidth={false}
                  style={{ flex: 1 }}
                  textStyle={{ fontWeight: "700" }}
                />
              </View>
            </SafeAreaView>
          </View>
        </View>
      </Modal>
    </>
  );
}
